import {DuplicateFieldsConfiguration} from "./DuplicateFieldsConfiguration";

/**
 * VerticalTableTemplate описывает параметры шаблона вывода колоночных услуг.
 */
export class VerticalTableTemplate {
  /**
   * Флаг позволяет выводить титульную строку перед основной таблицей.
   * В этой строке можно вывести поля или описание для колонок ниже.
   * Строка выводится даже при выключенном флаге, если в услугах
   * есть поля, которые необходимо выводить.
   */
  public IsNeedShowTitleRow: boolean = true

  /**
   * Название колонки с номером строки. Позволяет задавать локализованное
   * название.
   */
  public PositionColumnTitle: string = ""

  /**
   * Префикс номера строки. Позволяет задавать текст перед номером. Поддерживается
   * локализация значений.
   */
  public PositionPrefix: string = ""

  /**
   * Суффикс номера строки. Позволяет задавать текст после номера. Поддерживается
   * локализация значений.
   */
  public PositionSuffix: string = ""

  /**
   * Текст строки итогов. Выводится последней строкой. Поддерживает локализацию.
   * Содержит суммированное значение из поля колонки стоимости, а так же суммированные
   * данные по остальным колонкам.
   */
  public PositionSummaryText: string = ""

  /**
   * Поле, задающее количество строк в таблице. Должно быть числовым.
   * При его изменении будут добавляться/удаляться строки таблицы.
   */
  public RowQuantityField: string = ""

  /**
   * Поле для вывода курса в колонке Курс. Может быть только типа валюты или
   * числа. Если числовое, значение берется как есть, если валюта, то курс
   * рассчитывается автоматически, по отношении к курсу заказа.
   */
  public CourseColumnCurrencyField: string = ""

  /**
   * Флаг позволяет выводить суммарную стоимость услуг в шапке услуги. Если флаг
   * включен, то стоимость будет выводиться.
   */
  public IsNeedDisplayTotalsInHeader: boolean = false

  /**
   * Необходимость выводить текст вместо нулевого значения стоимости услуги.
   * Если не включен - значение в итогах будет выводиться как 0.
   */
  public IsNeedShowTextForZeroValues: boolean = false

  /**
   * Текст, выводимый при нулевой стоимости услуг. Выводится, если включен
   * флаг IsNeedShowTextForZeroValues. Может быть локализован.
   */
  public ZeroValuesText: string = ""

  /**
   * Флаг позволяет скрыть отображение колонки курса валюты
   */
  public IsCourseColumnHidden: boolean = false

  /**
   * Поле позволяет кастомизировать название колонки курса. Может быть локализовано.
   */
  public CourseColumnName: string = ""

  /**
   * Название колонки стоимости. Может быть локализовано.
   */
  public CostColumnName: string = ""

  /**
   * Поле, которое будет выводиться в колонке стоимость, может быть только числового
   * типа. Значение поля будет суммироваться и выводиться в итогах таблицы. Значение
   * по умолчанию, границы будут браться из настроек. Для поля не будет работать
   * автоматический расчет значений.
   */
  public CostColumnField: string = ""

  /**
   * Поле, которое будет содержать просуммированные значения поля CostColumnField
   * со всех строк таблицы. Устанавливается автоматически при изменении значения
   * в каком либо поле из строки.
   */
  public CostColumnSumField: string = ""

  /**
   * Услуга относится к разделу дополнительных услуг.
   */
  public IsAdditionService: boolean = false

  /**
   * Порядковый номер услуги. Используется для сортировки выводимых услуг.
   */
  public PositionInList: number = 0

  /**
   * IsNeedShowAddToBasketButton позволяет вывести в шаблоне услуг кнопку включения
   * услуги для добавления в корзину. Если кнопка не выводится, то услуга добавляется
   * совместно с группой. Если выведена, то пользователю для добавления ее необходимо
   * явно включить.
   */
  public IsNeedShowAddToBasketButton: boolean = false

  /**
   * IsNeedShowWrapper позволяет включать отступ вокруг таблицы. Если включено, то
   * таблица будет с отступом, нет - как у других таблиц.
   */
  public IsNeedShowWrapper: boolean = true

  /**
   * Если включить данный флаг, то услуга не будет отображаться в листинге, когда
   * ее стоимость < 0
   */
  public IsNotVisibleWhenPriceByRequest: boolean = false

  /**
   * DuplicateFields содержит подключенные для дублирования в
   * колонке "Количество" поля и их настройки.
   */
  public DuplicateFields: DuplicateFieldsConfiguration = DuplicateFieldsConfiguration.RestoreSettings({})

  /**
   * Заглушка для запрета прямого создания объектов данного типа
   * @private
   */
  private constructor() {
  }

  /**
   * RestoreSettings выполняет восстановление данных объекта настроек
   * Функция выполняет нормализацию значений настроек.
   * Функция дополняет недостающие настройки значениями по умолчанию.
   *
   * @param data
   */
  public static RestoreSettings(data: any): VerticalTableTemplate {
    let result = new VerticalTableTemplate();

    if (typeof data === "string") {
      data = JSON.parse(data)
    }

    if (typeof data !== "object") {
      return result;
    }

    Object.keys(result).map((key: keyof VerticalTableTemplate) => {
      // @ts-ignore
      result[key] = VerticalTableTemplate.GetCorrectFieldValue(key, data[key])
    })

    return result;
  }

  /**
   * GetCorrectFieldValue выполняет восстановление данных поля из переданного значения настроек.
   * Данный метод так же проводит валидацию значения каждой из настроек поля. Метод возвращает
   * значение по умолчанию, если значение передано не корректно.
   */
  public static GetCorrectFieldValue<T extends keyof VerticalTableTemplate>(field: T, data: any): any {
    let defaults = new VerticalTableTemplate()

    if (data === undefined) {
      return defaults[field]
    }

    switch (field) {
      case "PositionInList":
        return typeof data === "number" && data >= 0 ? data : defaults[field]
      case "IsNeedShowTitleRow":
      case "IsAdditionService":
      case "IsNeedDisplayTotalsInHeader":
      case "IsNeedShowTextForZeroValues":
      case "IsCourseColumnHidden":
      case "IsNeedShowAddToBasketButton":
      case "IsNeedShowWrapper":
      case "IsNotVisibleWhenPriceByRequest":
        return !!data
      case "PositionColumnTitle":
      case "PositionPrefix":
      case "PositionSuffix":
      case "PositionSummaryText":
      case "RowQuantityField":
      case "CourseColumnCurrencyField":
      case "ZeroValuesText":
      case "CourseColumnName":
      case "CostColumnName":
      case "CostColumnField":
      case "CostColumnSumField":
        return typeof data === "string" ? data : defaults[field]
      case "DuplicateFields":
        return DuplicateFieldsConfiguration.RestoreSettings(data)
    }

    return defaults[field];
  }
}